import type { SupabaseClient } from '../client'
import type { Database } from '../database.types'
import { SupabaseError } from '../errors'
import { unwrapList } from './_helpers'

export type ClientFile = Database['portal']['Tables']['client_files']['Row']

const BUCKET = 'client-files'
const COLUMNS =
  'id, project_id, initiative_id, uploaded_by, file_path, file_name, mime_type, size_bytes, tags, description, category, created_at'

export type ClientFileCategory = 'adm' | 'operational'

export type ListClientFilesOptions = {
  initiativeId?: number | null
  tag?: string | null
  category?: ClientFileCategory | null
}

/**
 * Lista arquivos do projeto, mais novos primeiro.
 * Filtros opcionais por initiative_id (passar `null` filtra "sem vinculo")
 * e por tag (igualdade em array).
 */
export async function listClientFilesByProject(
  client: SupabaseClient,
  projectId: number,
  options: ListClientFilesOptions = {},
): Promise<ClientFile[]> {
  let query = client
    .schema('portal')
    .from('client_files')
    .select(COLUMNS)
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })

  if (options.initiativeId === null) {
    query = query.is('initiative_id', null)
  } else if (typeof options.initiativeId === 'number') {
    query = query.eq('initiative_id', options.initiativeId)
  }
  if (options.tag) {
    query = query.contains('tags', [options.tag])
  }
  if (options.category) {
    query = query.eq('category', options.category)
  }

  return unwrapList(await query) as unknown as ClientFile[]
}

export type CreateClientFileInput = {
  projectId: number
  initiativeId?: number | null
  uploadedBy: string
  file: File
  tags?: string[]
  description?: string | null
  category?: ClientFileCategory
}

/**
 * Sobe um arquivo no bucket client-files e registra a linha em client_files.
 * Path no storage: `<project_id>/<timestamp>-<filename>` para evitar
 * colisoes e manter scoping por projeto (alinhado com a policy de RLS).
 */
export async function uploadClientFile(
  client: SupabaseClient,
  input: CreateClientFileInput,
): Promise<ClientFile> {
  const {
    projectId,
    initiativeId,
    uploadedBy,
    file,
    tags = [],
    description = null,
    category = 'operational',
  } = input
  const safeName = file.name.replace(/[^\w.-]+/g, '_')
  const path = `${projectId}/${Date.now()}-${safeName}`

  const { error: uploadError } = await client.storage
    .from(BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type || undefined })
  if (uploadError) throw new SupabaseError(uploadError.message, uploadError)

  const insertResult = await client
    .schema('portal')
    .from('client_files')
    .insert({
      project_id: projectId,
      initiative_id: initiativeId ?? null,
      uploaded_by: uploadedBy,
      file_path: path,
      file_name: file.name,
      mime_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
      tags,
      description,
      category,
    } as never)
    .select(COLUMNS)
    .single()

  if (insertResult.error) {
    // tenta limpar o arquivo orfão para nao deixar lixo no storage
    await client.storage.from(BUCKET).remove([path])
    throw new SupabaseError(insertResult.error.message, insertResult.error)
  }
  return insertResult.data as ClientFile
}

/**
 * Remove arquivo do storage e a linha de client_files.
 * Por policy, so o uploader original consegue apagar.
 */
export async function deleteClientFile(
  client: SupabaseClient,
  file: Pick<ClientFile, 'id' | 'file_path'>,
): Promise<void> {
  const { error: storageError } = await client.storage.from(BUCKET).remove([file.file_path])
  if (storageError) throw new SupabaseError(storageError.message, storageError)

  const { error: dbError } = await client
    .schema('portal')
    .from('client_files')
    .delete()
    .eq('id', file.id)
  if (dbError) throw new SupabaseError(dbError.message, dbError)
}

/**
 * Gera URL assinada (signed) para download privado. 1h de validade.
 */
export async function getClientFileSignedUrl(
  client: SupabaseClient,
  filePath: string,
  expiresInSeconds = 3600,
): Promise<string> {
  const result = await client.storage.from(BUCKET).createSignedUrl(filePath, expiresInSeconds)
  if (result.error) throw new SupabaseError(result.error.message, result.error)
  return result.data.signedUrl
}
